import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { compartilhamentosApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select, Toggle } from '../../components/ui/Input'
import { Card, CardHeader, CardBody, AvatarIcon } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { useContextoStore } from '../../store/contextoStore'
import { NivelPermissao, NIVEL_PERMISSAO_LABEL, StatusCompartilhamento, STATUS_COMPARTILHAMENTO_LABEL } from '../../lib/enums'
import { formatDate } from '../../lib/utils'
import type { Compartilhamento, Permissoes } from '../../types'

const MODULOS: { chave: keyof Permissoes; label: string; icon: string }[] = [
  { chave: 'contas', label: 'Contas', icon: 'ri-bank-line' },
  { chave: 'transacoes', label: 'Lançamentos e categorias', icon: 'ri-exchange-dollar-line' },
  { chave: 'cartoes', label: 'Cartões', icon: 'ri-bank-card-line' },
  { chave: 'orcamento', label: 'Orçamento', icon: 'ri-pie-chart-2-line' },
  { chave: 'metas', label: 'Metas', icon: 'ri-focus-3-line' },
  { chave: 'relatorios', label: 'Relatórios e visão geral', icon: 'ri-bar-chart-box-line' },
  { chave: 'notificacoes', label: 'Notificações de contas atualizadas', icon: 'ri-notification-3-line' },
  { chave: 'itens', label: 'Itens comprados (notas fiscais)', icon: 'ri-price-tag-3-line' },
  { chave: 'saude', label: 'Itens de saúde (remédios)', icon: 'ri-capsule-line' },
  { chave: 'lista', label: 'Listas de compras', icon: 'ri-shopping-cart-2-line' },
  { chave: 'veiculos', label: 'Veículos', icon: 'ri-car-line' },
]

const nivelDe = (p: Permissoes, chave: keyof Permissoes): NivelPermissao => p[chave] ?? NivelPermissao.Nenhum

const PERMISSOES_PADRAO: Permissoes = {
  contas: NivelPermissao.Ver,
  transacoes: NivelPermissao.Ver,
  cartoes: NivelPermissao.Nenhum,
  orcamento: NivelPermissao.Ver,
  metas: NivelPermissao.Nenhum,
  relatorios: NivelPermissao.Ver,
  notificacoes: NivelPermissao.Nenhum,
  itens: NivelPermissao.Ver,
  saude: NivelPermissao.Nenhum,
  lista: NivelPermissao.Nenhum,
  veiculos: NivelPermissao.Nenhum,
}

/** Libera itens de saúde só com o consentimento explícito do dono (uma vez por pessoa). */
function consentirSaude(p: Permissoes, jaTinha: boolean): boolean {
  if (p.saude === NivelPermissao.Nenhum) return false
  if (jaTinha) return true
  if (!window.confirm('Itens de saúde mostram os remédios e vitaminas que você compra. Só libere para quem você quer que saiba. Você pode tirar essa permissão quando quiser.\n\nVocê autoriza?')) throw new Error('Para mostrar itens de saúde é preciso confirmar o consentimento.')
  return true
}

const STATUS_VARIANT: Record<number, 'warning' | 'income' | 'expense' | 'muted'> = {
  [StatusCompartilhamento.Pendente]: 'warning',
  [StatusCompartilhamento.Aceito]: 'income',
  [StatusCompartilhamento.Recusado]: 'expense',
  [StatusCompartilhamento.Revogado]: 'muted',
}

export function CompartilhamentoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const selecionados = useContextoStore((s) => s.selecionados)
  const alternarConta = useContextoStore((s) => s.alternarConta)

  const [convite, setConvite] = useState<{ email: string; permissoes: Permissoes } | null>(null)
  const [editando, setEditando] = useState<Compartilhamento | null>(null)
  const [permissoesEdicao, setPermissoesEdicao] = useState<Permissoes>(PERMISSOES_PADRAO)
  const [erro, setErro] = useState<string | null>(null)
  const [paraRevogar, setParaRevogar] = useState<Compartilhamento | null>(null)

  const { data: enviados = [], isLoading: loadingEnviados } = useQuery({
    queryKey: ['compartilhamentos', 'enviados'],
    queryFn: compartilhamentosApi.enviados,
  })
  const { data: recebidos = [], isLoading: loadingRecebidos } = useQuery({
    queryKey: ['compartilhamentos', 'recebidos'],
    queryFn: compartilhamentosApi.recebidos,
  })

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['compartilhamentos'] })
    qc.invalidateQueries({ queryKey: ['contextos'] })
  }

  const convidar = useMutation({
    mutationFn: () => compartilhamentosApi.convidar({ email: convite!.email.trim(), permissoes: convite!.permissoes, consentimentoSaude: consentirSaude(convite!.permissoes, false) }),
    onSuccess: () => {
      invalidate()
      toast.success('Convite enviado.')
      setConvite(null)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const atualizarPermissoes = useMutation({
    mutationFn: () => compartilhamentosApi.atualizarPermissoes(editando!.id, permissoesEdicao, consentirSaude(permissoesEdicao, editando!.permissoes.saude !== NivelPermissao.Nenhum)),
    onSuccess: () => {
      invalidate()
      toast.success('Permissões atualizadas.')
      setEditando(null)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const responder = useMutation({
    mutationFn: ({ id, aceitar }: { id: string; aceitar: boolean }) =>
      aceitar ? compartilhamentosApi.aceitar(id) : compartilhamentosApi.recusar(id),
    onSuccess: (_, v) => {
      invalidate()
      toast.success(v.aceitar ? 'Convite aceito. A conta já aparece no seletor da topbar.' : 'Convite recusado.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const liberarCartoes = useMutation({
    mutationFn: ({ id, liberado }: { id: string; liberado: boolean }) => compartilhamentosApi.definirCartoesDoConvidado(id, liberado),
    onSuccess: (_, v) => {
      invalidate()
      qc.invalidateQueries({ queryKey: ['cartoes-utilizaveis'] })
      toast.success(v.liberado ? 'Seus cartões estão liberados para compras divididas.' : 'Seus cartões não aparecem mais nas divisões.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const revogar = useMutation({
    mutationFn: (id: string) => compartilhamentosApi.revogar(id),
    onSuccess: (_, id) => {
      const alvo = [...enviados, ...recebidos].find((c) => c.id === id)
      const selecionada = alvo && selecionados.find((s) => s.ownerUserId === alvo.ownerUserId)
      if (selecionada) alternarConta(selecionada)
      invalidate()
      toast.success('Acesso encerrado.')
      setParaRevogar(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function abrirConvite() {
    setConvite({ email: '', permissoes: { ...PERMISSOES_PADRAO } })
    setErro(null)
  }

  function abrirEdicao(c: Compartilhamento) {
    setEditando(c)
    setPermissoesEdicao(c.permissoes)
    setErro(null)
  }

  return (
    <>
      <PageHeader title="Compartilhamento" />

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader
            title="Pessoas com acesso à minha conta"
            icon="ri-user-shared-line"
            actions={<Button icon="ri-mail-add-line" onClick={abrirConvite}>Convidar</Button>}
          />
          {loadingEnviados ? (
            <LoadingState />
          ) : enviados.length === 0 ? (
            <EmptyState icon="ri-user-shared-line" message="Você ainda não compartilhou sua conta." action={<Button variant="soft-primary" icon="ri-mail-add-line" onClick={abrirConvite}>Convidar alguém</Button>} />
          ) : (
            <CardBody className="space-y-3 p-4">
              {enviados.map((c) => (
                <div key={c.id} className="rounded border border-border/70 p-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <AvatarIcon icon="ri-user-line" tone={c.status === StatusCompartilhamento.Aceito ? 'income' : 'muted'} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-white">{c.guestNome ?? c.guestEmail}</p>
                      <p className="truncate text-2xs text-muted">{c.guestEmail} · convite de {formatDate(c.createdAt)}</p>
                    </div>
                    <Badge variant={STATUS_VARIANT[c.status] ?? 'muted'}>{STATUS_COMPARTILHAMENTO_LABEL[c.status]}</Badge>
                    <div className="flex">
                      <IconButton icon="ri-shield-keyhole-line" label={`Editar permissões de ${c.guestEmail}`} tone="info" onClick={() => abrirEdicao(c)} />
                      <IconButton icon="ri-delete-bin-5-fill" label={`Encerrar acesso de ${c.guestEmail}`} tone="danger" onClick={() => setParaRevogar(c)} />
                    </div>
                  </div>
                  <ResumoPermissoes permissoes={c.permissoes} />
                </div>
              ))}
            </CardBody>
          )}
        </Card>

        <Card>
          <CardHeader title="Contas compartilhadas comigo" icon="ri-user-received-line" />
          {loadingRecebidos ? (
            <LoadingState />
          ) : recebidos.length === 0 ? (
            <EmptyState icon="ri-user-received-line" message="Ninguém compartilhou uma conta com você ainda." />
          ) : (
            <CardBody className="space-y-3 p-4">
              {recebidos.map((c) => (
                <div key={c.id} className="rounded border border-border/70 p-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <AvatarIcon icon="ri-user-line" tone={c.status === StatusCompartilhamento.Aceito ? 'income' : 'warning'} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-white">{c.ownerNome}</p>
                      <p className="truncate text-2xs text-muted">{c.ownerEmail}</p>
                    </div>
                    <Badge variant={STATUS_VARIANT[c.status] ?? 'muted'}>{STATUS_COMPARTILHAMENTO_LABEL[c.status]}</Badge>
                  </div>
                  <ResumoPermissoes permissoes={c.permissoes} />
                  {c.status === StatusCompartilhamento.Aceito && (
                    <div className="mt-3 rounded bg-surface2 p-3">
                      <Toggle
                        label="Liberar meus cartões"
                        checked={!!c.cartoesDoConvidadoLiberados}
                        disabled={liberarCartoes.isPending}
                        onChange={(liberado) => liberarCartoes.mutate({ id: c.id, liberado })}
                      />
                      <p className="mt-1 text-2xs text-muted">{c.ownerNome} poderá usar os seus cartões ao dividir uma compra. Só você liga e desliga isto.</p>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {c.status === StatusCompartilhamento.Pendente && (
                      <>
                        <Button size="sm" variant="soft-success" icon="ri-check-line" loading={responder.isPending} onClick={() => responder.mutate({ id: c.id, aceitar: true })}>Aceitar</Button>
                        <Button size="sm" variant="light" icon="ri-close-line" loading={responder.isPending} onClick={() => responder.mutate({ id: c.id, aceitar: false })}>Recusar</Button>
                      </>
                    )}
                    {c.status === StatusCompartilhamento.Aceito && (
                      <>
                        <Button
                          size="sm"
                          variant={selecionados.some((s) => s.ownerUserId === c.ownerUserId) ? 'soft-primary' : 'light'}
                          icon={selecionados.some((s) => s.ownerUserId === c.ownerUserId) ? 'ri-checkbox-circle-line' : 'ri-add-line'}
                          onClick={() => alternarConta({ ownerUserId: c.ownerUserId, ownerNome: c.ownerNome, ownerEmail: c.ownerEmail, permissoes: c.permissoes })}
                        >
                          {selecionados.some((s) => s.ownerUserId === c.ownerUserId) ? 'Somando nas telas' : 'Somar nas minhas telas'}
                        </Button>
                        <Button size="sm" variant="soft-danger" icon="ri-logout-box-line" onClick={() => setParaRevogar(c)}>Sair da conta</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </CardBody>
          )}
        </Card>
      </div>

      <Modal
        open={!!convite}
        onClose={() => setConvite(null)}
        title="Convidar para minha conta"
        footer={
          <>
            <Button variant="light" onClick={() => setConvite(null)}>Cancelar</Button>
            <Button
              icon="ri-mail-send-line"
              loading={convidar.isPending}
              onClick={() => convite?.email.trim() ? convidar.mutate() : setErro('Informe o e-mail de quem você quer convidar.')}
            >
              Enviar convite
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="E-mail da pessoa"
            type="email"
            value={convite?.email ?? ''}
            onChange={(e) => setConvite((c) => c && { ...c, email: e.target.value })}
            placeholder="pessoa@email.com"
            autoFocus
          />
          <p className="text-2xs text-muted">Se ela ainda não tiver conta no Walleon, o convite fica guardado e aparece assim que ela se cadastrar com esse e-mail.</p>
          <MatrizPermissoes
            permissoes={convite?.permissoes ?? PERMISSOES_PADRAO}
            onChange={(p) => setConvite((c) => c && { ...c, permissoes: p })}
          />
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title={`Permissões · ${editando?.guestNome ?? editando?.guestEmail ?? ''}`}
        footer={
          <>
            <Button variant="light" onClick={() => setEditando(null)}>Cancelar</Button>
            <Button icon="ri-save-line" loading={atualizarPermissoes.isPending} onClick={() => atualizarPermissoes.mutate()}>Salvar</Button>
          </>
        }
      >
        <div className="space-y-4">
          <MatrizPermissoes permissoes={permissoesEdicao} onChange={setPermissoesEdicao} />
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraRevogar}
        onClose={() => setParaRevogar(null)}
        onConfirm={() => paraRevogar && revogar.mutate(paraRevogar.id)}
        loading={revogar.isPending}
        title="Encerrar compartilhamento?"
        confirmLabel="Sim, encerrar"
        message={<>O acesso entre <strong className="text-white">{paraRevogar?.ownerNome}</strong> e <strong className="text-white">{paraRevogar?.guestNome ?? paraRevogar?.guestEmail}</strong> será removido.</>}
      />
    </>
  )
}

function ResumoPermissoes({ permissoes }: { permissoes: Permissoes }) {
  const ativos = MODULOS.filter((m) => nivelDe(permissoes, m.chave) !== NivelPermissao.Nenhum)
  if (ativos.length === 0) return <p className="mt-2 text-2xs text-muted">Sem módulos liberados.</p>

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {ativos.map((m) => (
        <Badge key={m.chave} variant={nivelDe(permissoes, m.chave) === NivelPermissao.Editar ? 'primary' : 'muted'}>
          <i className={m.icon} aria-hidden /> {m.label}: {nivelDe(permissoes, m.chave) === NivelPermissao.Editar ? 'editar' : 'ver'}
        </Badge>
      ))}
    </div>
  )
}

function MatrizPermissoes({ permissoes, onChange }: { permissoes: Permissoes; onChange: (p: Permissoes) => void }) {
  return (
    <div>
      <span className="form-label">O que essa pessoa pode acessar</span>
      <div className="divide-y divide-border/60 rounded border border-border/70">
        {MODULOS.map((m) => (
          <div key={m.chave} className="flex items-center gap-3 px-3 py-2">
            <i className={`${m.icon} text-base text-muted`} aria-hidden />
            <span className="flex-1 text-13 text-white">{m.label}</span>
            <div className="w-36">
              <Select
                value={nivelDe(permissoes, m.chave)}
                aria-label={`Permissão de ${m.label}`}
                onChange={(e) => onChange({ ...permissoes, [m.chave]: Number(e.target.value) as NivelPermissao })}
              >
                {Object.entries(NIVEL_PERMISSAO_LABEL)
                  .filter(([valor]) => (m.chave !== 'itens' && m.chave !== 'saude') || Number(valor) !== NivelPermissao.Editar)
                  .map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
              </Select>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-2xs text-muted">Para a pessoa usar os seus cartões numa compra dividida, deixe Cartões em "Pode editar". Os cartões dela só aparecem para você se ela liberar.</p>
    </div>
  )
}
